import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAdminSupportThreads, useUpdateSupportThread } from "@/hooks/useSupportThreads";
import { ThreadView } from "@/components/support/ThreadView";
import { ArrowLeft, CheckCircle, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const CATEGORIES = [
  { value: "account", label: "Account" },
  { value: "courses", label: "Courses" },
  { value: "mentorship", label: "Mentorship" },
  { value: "technical", label: "Technical" },
  { value: "other", label: "Other" },
];

interface AdminThreadDetailProps {
  threadId: string;
  onBack: () => void;
}

export function AdminThreadDetail({ threadId, onBack }: AdminThreadDetailProps) {
  const { data: threads } = useAdminSupportThreads();
  const thread = threads?.find((t) => t.id === threadId);
  const updateThread = useUpdateSupportThread();

  // Fetch admin users for assignment from user_roles table
  const { data: adminUsers } = useQuery({
    queryKey: ["admin_users_list"],
    queryFn: async () => {
      const { data: adminRoles } = await supabase
        .from("user_roles")
        .select("user_id")
        .eq("role", "admin");

      if (!adminRoles?.length) return [];

      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", adminRoles.map((a) => a.user_id));

      return profiles || [];
    },
  });

  const handleStatusChange = async (status: "open" | "awaiting_user" | "resolved") => {
    try {
      await updateThread.mutateAsync({
        threadId,
        updates: { status },
      });
      toast.success(`Status updated to ${status.replace("_", " ")}`);
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleCategoryChange = async (category: string) => {
    try {
      await updateThread.mutateAsync({
        threadId,
        updates: { category },
      });
      toast.success("Category updated");
    } catch (error) {
      toast.error("Failed to update category");
    }
  };

  const handleAssignmentChange = async (adminId: string) => {
    try {
      await updateThread.mutateAsync({
        threadId,
        updates: { assigned_admin_id: adminId === "unassigned" ? null : adminId },
      });
      toast.success("Assignment updated");
    } catch (error) {
      toast.error("Failed to update assignment");
    }
  };

  if (!thread) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          Thread not found
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Thread Info Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={onBack}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Inbox
            </Button>
            <div className="flex-1" />
            <Badge
              className={cn(
                "text-sm",
                thread.status === "open" && "bg-blue-100 text-blue-800",
                thread.status === "awaiting_user" && "bg-amber-100 text-amber-800",
                thread.status === "resolved" && "bg-green-100 text-green-800"
              )}
            >
              {thread.status.replace("_", " ")}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            <div>
              <Label className="text-xs text-muted-foreground">User</Label>
              <p className="font-medium">{thread.user_name}</p>
              <p className="text-xs text-muted-foreground">{thread.user_email}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Subject</Label>
              <p className="font-medium">{thread.subject || "No subject"}</p>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Category</Label>
              <Select
                value={thread.category || ""}
                onValueChange={handleCategoryChange}
              >
                <SelectTrigger className="mt-1 h-8">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Assigned To</Label>
              <Select
                value={thread.assigned_admin_id || "unassigned"}
                onValueChange={handleAssignmentChange}
              >
                <SelectTrigger className="mt-1 h-8">
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {adminUsers?.map((admin) => (
                    <SelectItem key={admin.id} value={admin.id}>
                      {admin.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="flex gap-2">
            {thread.status !== "resolved" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleStatusChange("resolved")}
                disabled={updateThread.isPending}
                className="text-green-700 border-green-300 hover:bg-green-50"
              >
                {updateThread.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="h-4 w-4 mr-2" />
                )}
                Mark Resolved
              </Button>
            )}
            {thread.status === "resolved" && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleStatusChange("open")}
                disabled={updateThread.isPending}
              >
                {updateThread.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RotateCcw className="h-4 w-4 mr-2" />
                )}
                Reopen
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Messages Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Conversation</CardTitle>
        </CardHeader>
        <CardContent>
          <ThreadView threadId={threadId} onBack={() => {}} senderRole="admin" />
        </CardContent>
      </Card>
    </div>
  );
}
